import { Outlet, useNavigate } from 'react-router-dom'
// import { useNavigate } from 'react-router-dom';
import { Box, Drawer, AppBar, useMediaQuery, useTheme } from '@mui/material'
import { useAuth, useBrowserQuery, NotificationSocketBridge } from '@/hooks/common'
import { useEffect, useMemo } from 'react'
import { AxiosProvider } from '@/context/Axios'
import { SubscriptionProvider } from '@/context/SubscriptionContext'
import DashboardSideBar from './SideBar'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import ResponsiveNavBar from './ResponsiveNavBar'
import { useLocation } from 'react-router-dom'
import { TopicType } from '@/utils/constants'

export default function DashboardLayout() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const query = useBrowserQuery()
  const theme = useTheme()
  const lessThanSmall = useMediaQuery(theme.breakpoints.down('md'))
  const { resetTopicList, setMyDMConnections } = useAppStore()
  const [sidebarCollapsed, showSideMenuDrawer] = useAppStore(
    (state) => [state.home.sidebarCollapsed, state.app.showSideMenuDrawer],
    shallow,
  )

  const drawerWidth = sidebarCollapsed ? '88px' : '240px'
  const { setShowSideMenuDrawer } = useAppStore()

  useEffect(() => {
    resetTopicList()
    setMyDMConnections([])
  }, [])

  useEffect(() => {
    if (!user?.userID) {
      navigate('/login')
    }
  }, [user?.userID])

  const showSideMenu = useMemo(() => {
    return !(
      pathname.startsWith('/pattern') ||
      (pathname.startsWith('/preview') && query.get('topicType') != TopicType.ALBUM.toString())
    )
  }, [pathname, query])

  const showTopMenu = useMemo(() => {
    return !(
      pathname.startsWith('/pattern') ||
      (pathname.startsWith('/preview') && query.get('topicType') != TopicType.ALBUM.toString())
    )
  }, [pathname, query])

  // Check if we're on the /workspace/all page to remove horizontal padding
  const isWorkspaceAllPage = pathname === '/workspace/all'

  return (
    <SubscriptionProvider>
    <AxiosProvider>
      {/* Connects once per mount of this shell and survives navigation
          between its nested routes (see AlbumDesignRoot for the other
          always-on shell that mounts the same bridge). */}
      <NotificationSocketBridge />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'relative',
        }}
      >
        {/* Fixed Dark Header at the very top */}
        {showTopMenu && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              height: '56px',
              backgroundColor: '#1d1d1d',
              width: '100%',
              boxShadow: 'none', // Remove shadow for seamless look
            }}
          >
            <ResponsiveNavBar />
          </Box>
        )}

        {/* Main Content Area */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            height: '100vh',
            backgroundColor: '#1d1d1d',
            position: 'relative',
            paddingTop: showTopMenu ? '56px' : '0px', // Add padding for fixed header
          }}
        >
          {/* Left Drawer */}
          {showSideMenu && (
            <Drawer
              sx={{
                width: drawerWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: drawerWidth,
                  ml: 0,
                  boxSizing: 'border-box',
                  borderRight: 'none',
                  backgroundColor: '#1d1d1d', // Match the app shell background
                  boxShadow: 'none', // Remove drawer shadow, let sidebar handle it
                  zIndex: 999, // Below fixed header but above content
                  top: showTopMenu ? '56px' : '0px', // Position below fixed header
                  height: showTopMenu ? 'calc(100vh - 56px)' : '100vh',
                  position: 'fixed', // Make sidebar fixed too
                },
              }}
              open={lessThanSmall ? showSideMenuDrawer : true}
              variant={lessThanSmall ? 'temporary' : 'permanent'}
              onClose={() => setShowSideMenuDrawer(false)}
              anchor='left'
            >
              <DashboardSideBar />
            </Drawer>
          )}

          {/* Conditional Layout: Full-width for pattern/preview pages, rounded container for others */}
          {showSideMenu || showTopMenu ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                padding: '0 16px 0 0px', // No top/bottom padding for fluent full canvas
                minHeight: 0, // Allow flex child to shrink
              }}
            >
              {/* White Content Container with top border radius */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  borderRadius: '16px 16px 0 0', // Top corners rounded, bottom extends full
                  overflow: 'hidden', // Hide overflow to maintain border radius
                  backgroundColor: '#ffffff',
                  boxShadow: '0 16px 44px rgba(25,35,80,.08), 0 2px 8px rgba(25,35,80,.06)',
                }}
              >
                {/* Scrollable content area */}
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    },
                  }}
                >
                  {/* Content Layer - Conditional padding based on route */}
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      minHeight: '100%',
                      padding: isWorkspaceAllPage ? '0' : '20px',
                      paddingTop: isWorkspaceAllPage ? '20px' : '0px',
                    }}
                  >
                    <Outlet />
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            /* Full-width layout for pattern/preview pages */
            <Box
              flex={1}
              sx={{
                position: 'relative',
                height: '100%',
                backgroundColor: 'transparent', // No background for full-width pages
              }}
            >
              <Outlet />
            </Box>
          )}
        </Box>
      </Box>
    </AxiosProvider>
    </SubscriptionProvider>
  )
}

